/**
 * UnsavedChangesGuardModal — shared "you have unsaved changes" confirmation
 * (escape-layer contract, issue #3938).
 *
 * Rendered by an edit modal when the user tries to close (Esc / X / backdrop)
 * while the form is dirty, so dirty edits are never silently discarded. Stateless
 * presentational leaf: the host owns the `show` flag and supplies explicit
 * `onSave` / `onDiscard` callbacks (no `.contact-save-btn` querySelector coupling
 * — each host wires its own submit/close).
 *
 * The root carries a `*-overlay` class so useModulePaneEscapeClose's DOM guard
 * treats it as a modal and never closes the module pane underneath it.
 */
interface UnsavedChangesGuardModalProps {
  show: boolean;
  /** Discard edits and close the editor. */
  onDiscard: () => void;
  /** Persist edits (host triggers its own save/submit). */
  onSave: () => void;
  /** Dismiss the confirmation and return to editing. */
  onCancel: () => void;
}

export function UnsavedChangesGuardModal({ show, onDiscard, onSave, onCancel }: UnsavedChangesGuardModalProps) {
  if (!show) return null;

  return (
    <div
      className="unsaved-changes-modal-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2147483647, padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--color-surface)', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)', textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--color-text)', margin: '0 0 6px' }}>Unsaved changes</p>
        <p style={{ fontSize: 13, color: '#4a4d51', margin: '0 0 20px', lineHeight: 1.5 }}>
          You have unsaved changes. Do you want to save before closing?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            onClick={() => { onCancel(); onDiscard(); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #DADCE0', background: 'var(--color-surface)', fontSize: 13, cursor: 'pointer', color: '#4a4d51' }}
          >
            Discard
          </button>
          <button
            onClick={() => { onCancel(); onSave(); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
